import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import TypedEditor from "../../components/TypedEditor";
import MedicineSuggestionPanel from "../../components/MedicineSuggestionPanel";
import DiseaseSuggestionPanel  from "../../components/DiseaseSuggestionPanel";
import { useSearchParams } from "react-router-dom";
import FollowUpModal from "../../components/FollowUpModal"; // NEW



/* ===== CONSTANTS ===== */
// Final printable paper size
const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1123;

// Bigger drawing space (doctor writes here)
const DRAW_WIDTH = 900;
const DRAW_HEIGHT = 1275;

// Print size (keep same)
const PRINT_WIDTH = PAGE_WIDTH;
const PRINT_HEIGHT = PAGE_HEIGHT;

/* ================================================================
   STRUCTURED RX / ADVICE HELPERS
   ----------------------------------------------------------------
   The ADVICE box is stored on the backend as a plain HTML string
   (typed.advice). To keep that contract (no backend/model changes,
   no print-component changes needed), the structured medicine rows
   are serialized into an HTML string:
     <!--RX_STRUCTURED:<json>-->  (hidden marker, used to re-hydrate
                                    the form fields on reload)
     <ol>...</ol>                (the actual visible/printable list)
   Any legacy free-text advice (typed before this feature existed)
   is preserved and appended below the structured list so nothing
   is ever silently lost.
================================================================= */
let __rxIdSeed = 0;
const makeMedicineId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `rx_${Date.now()}_${__rxIdSeed++}`;

const emptyMedicineRow = () => ({
  id: makeMedicineId(),
  name: "",
  dosage: "",
  frequency: "",
  days: "",
  anupana: "",
  notes: "",
  remark: "",
});

/* ================================================================
   BILINGUAL SMART DISPLAY
   ----------------------------------------------------------------
   Predefined Frequency / Anupana values show as "English (Hindi)".
   If the doctor types a custom value (not in this list), it is
   shown exactly as typed — no bilingual pairing is invented.
================================================================= */
const FREQUENCY_OPTIONS = [
  { value: "OD",  en: "Once Daily",    hi: "दिन में एक बार" },
  { value: "BD",  en: "Twice Daily",   hi: "दिन में दो बार" },
  { value: "TDS", en: "Thrice Daily",  hi: "दिन में तीन बार" },
  { value: "QID", en: "Four Times Daily", hi: "दिन में चार बार" },
  { value: "HS",  en: "At Bedtime",    hi: "सोते समय" },
  { value: "SOS", en: "If Needed",     hi: "आवश्यकतानुसार" },
  { value: "AC",  en: "Before Food",   hi: "भोजन से पहले" },
  { value: "PC",  en: "After Food",    hi: "भोजन के बाद" },
];

const ANUPANA_OPTIONS = [
  { value: "Warm Water", en: "Warm Water", hi: "गुनगुना पानी" },
  { value: "Milk",       en: "Milk",       hi: "दूध" },
  { value: "Honey",      en: "Honey",      hi: "शहद" },
  { value: "Ghee",       en: "Ghee",       hi: "घी" },
  { value: "Buttermilk", en: "Buttermilk", hi: "छाछ" },
  { value: "Rice Water", en: "Rice Water", hi: "चावल का पानी" },
  { value: "Plain Water",en: "Plain Water",hi: "सादा पानी" },
];

/* Returns "English (Hindi)" for a known predefined value, otherwise
   returns the raw text untouched (custom doctor-typed value). */
function bilingualLabel(value, options) {
  const v = (value || "").trim();
  if (!v) return "";
  const match = options.find(o => o.value.toLowerCase() === v.toLowerCase());
  return match ? `${match.en} (${match.hi})` : v;
}

const escapeHtml = (str = "") =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const RX_MARKER_RE = /<!--RX_STRUCTURED:([\s\S]*?)-->/;

/* Split a saved advice HTML string back into { medicines, legacyHtml } */
function parseAdviceHtml(html) {
  if (!html || !html.trim()) return { medicines: [emptyMedicineRow()], legacyHtml: "" };

  const match = html.match(RX_MARKER_RE);
  if (match) {
    try {
      const parsed = JSON.parse(decodeURIComponent(match[1]));
      const medicines = Array.isArray(parsed) && parsed.length
        ? parsed.map(m => ({ ...emptyMedicineRow(), ...m }))
        : [emptyMedicineRow()];
      // anything after the marker+list that isn't part of our generated
      // markup is treated as legacy/extra content and preserved as-is
      const legacyMatch = html.match(/<div class="rx-legacy-advice"[^>]*>([\s\S]*)<\/div>\s*$/);
      const legacyHtml = legacyMatch ? legacyMatch[1] : "";
      return { medicines, legacyHtml };
    } catch {
      /* fall through to legacy handling below */
    }
  }

  // No marker found => this is pre-existing free-text advice HTML from
  // before structured rows existed. Preserve it untouched.
  return { medicines: [emptyMedicineRow()], legacyHtml: html };
}

/* Build the printable/previewable advice HTML from structured state */
function buildAdviceHtml(medicines = [], legacyHtml = "") {
  const rows = (medicines || []).filter(m =>
    [m.name, m.dosage, m.frequency, m.days, m.anupana, m.notes, m.remark].some(v => (v || "").trim())
  );

  // Nothing entered at all → keep typed.advice truly empty so the
  // print engine's "skip empty box" logic behaves exactly as before.
  if (!rows.length && (!legacyHtml || !legacyHtml.trim())) return "";

  const marker = `<!--RX_STRUCTURED:${encodeURIComponent(JSON.stringify(medicines))}-->`;

  const listHtml = rows.length
    ? `<ol class="rx-medicine-list" style="padding-left:18px;margin:0;">` +
      rows.map(m => {
        const freqLabel = bilingualLabel(m.frequency, FREQUENCY_OPTIONS);
        const anupanaLabel = bilingualLabel(m.anupana, ANUPANA_OPTIONS);
        const headline = [
          m.dosage,
          freqLabel,
          m.days ? `${m.days} Days` : "",
          anupanaLabel,
        ]
          .filter(v => (v || "").trim())
          .map(escapeHtml)
          .join(" &middot; ");
        return (
          `<li style="margin-bottom:6px;">` +
            `<b>${escapeHtml(m.name || "Medicine")}</b>` +
            (headline ? ` &mdash; ${headline}` : "") +
            (m.notes && m.notes.trim()
              ? `<br/><span style="font-size:0.9em;color:#4b5563;">${escapeHtml(m.notes)}</span>`
              : "") +
            (m.remark && m.remark.trim()
              ? `<br/><span style="font-size:0.85em;color:#6b7280;font-style:italic;">${escapeHtml(m.remark)}</span>`
              : "") +
          `</li>`
        );
      }).join("") +
      `</ol>`
    : `<p style="color:#9ca3af;">No medicines added</p>`;

  const legacyBlock = legacyHtml && legacyHtml.trim()
    ? `<div class="rx-legacy-advice" style="margin-top:10px;padding-top:8px;border-top:1px dashed #d1d5db;">${legacyHtml}</div>`
    : "";

  return marker + listHtml + legacyBlock;
}

/* Small combo: dropdown of predefined bilingual values + "Custom…" free text.
   Keeps the underlying value a plain string (backward compatible with the
   existing medicine-row schema) — no separate Hindi field is ever created. */
function BilingualSelect({ value, onChange, options, placeholder }) {
  const isKnown = options.some(o => o.value.toLowerCase() === (value || "").toLowerCase());
  const isCustomMode = value && !isKnown;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <select
        className="input"
        style={{ fontSize: 12 }}
        value={isCustomMode ? "__custom__" : (value || "")}
        onChange={(e) => {
          if (e.target.value === "__custom__") { onChange(""); return; }
          onChange(e.target.value);
        }}
      >
        <option value="">{placeholder}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.en} ({o.hi})</option>
        ))}
        <option value="__custom__">Custom…</option>
      </select>
      {isCustomMode && (
        <input
          className="input"
          style={{ fontSize: 12 }}
          placeholder="Type custom value"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

export default function PrescriptionPad() {
  const { patientId, rxId } = useParams();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

const visitType = searchParams.get("visitType") || "new";
const parentRxId = searchParams.get("parentRxId") || null
const draftKey = `rxDraft_${patientId}_${visitType}`;
const today = new Date().toISOString().slice(0,10);
const visitLockKey = `visitLock_${patientId}_${visitType}_${today}`;

  const [patient, setPatient] = useState(null);

  /* ── Doctor info for medicine suggestion access check ── */
  const doctorUser = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("doctorUser") || "null"); }
    catch { return null; }
  }, []);
  const [settings, setSettings] = useState({
  columnRatio: 30,
  showConsultantHeader: true,
  showFooter: true,
  topOffset: 0,
  footerOffset: 10,
  headerBox: {
    content: "",
    style: { fontFamily: "Times New Roman", fontSize: 18, lineHeight: 1.5, paragraphSpacing: 8, textAlign: "center" },
    tools: { bold: false, italic: false, underline: false, highlight: false }
  },
  footerBox: {
    content: "",
    style: { fontFamily: "Times New Roman", fontSize: 14, lineHeight: 1.5, paragraphSpacing: 8, textAlign: "right" },
    tools: { bold: false, italic: false, underline: false, highlight: false }
  },
 typedEditor: {
  complaints: {
    style: {
      fontFamily: "Times New Roman",
      fontSize: 16,
      lineHeight: 1.5,
      paragraphSpacing: 8,
      textAlign: "left",
      bulletType: "disc",
      numberType: "decimal"
    },
    tools: {}
  },
  diagnosis: {
    style: {
      fontFamily: "Times New Roman",
      fontSize: 16,
      lineHeight: 1.5,
      paragraphSpacing: 8,
      textAlign: "left",
      bulletType: "disc",
      numberType: "decimal"
    },
    tools: {}
  },
  advice: {
    style: {
      fontFamily: "Times New Roman",
      fontSize: 16,
      lineHeight: 1.5,
      paragraphSpacing: 8,
      textAlign: "left",
      bulletType: "disc",
      numberType: "decimal"
    },
    tools: {}
  },
  notes: {
    style: {
      fontFamily: "Times New Roman",
      fontSize: 16,
      lineHeight: 1.5,
      paragraphSpacing: 8,
      textAlign: "left",
      bulletType: "disc",
      numberType: "decimal"
    },
    tools: {}
  }
},
  doctorDetails: {}
});
  const [mode, setMode] = useState("typed");
  const [pageIndex, setPageIndex] = useState(0);
const autosaveTimerRef = useRef(null);
const lastSavedRef = useRef("");
const savingRef = useRef(false);
const creatingRef = useRef(false);
const autoSaveFailRef = useRef(0);          // consecutive fail counter
const [showSaveWarn, setShowSaveWarn] = useState(false);
const [draftTick,setDraftTick] = useState(0);

  
  const [tool, setTool] = useState("pen");

  const [eraserWidth, setEraserWidth] = useState(20);
  /* Eraser mode: "pixel" = existing destination-out eraser (unchanged
     behaviour). "stroke" = new — tap/drag over a whole stroke to
     delete it cleanly, no ragged pixel fragments left behind. */
  const [eraserMode, setEraserMode] = useState("pixel");
  const strokeErasingRef = useRef(false);
  /* Single-level "undo my last object-erase" — separate from the
     existing pen undo/redo stack so neither system interferes with
     the other. Cleared whenever a new stroke is drawn. */
  const lastEraseRef = useRef(null);
  /* Zoom (canvas mode only). Pan is via the existing scroll container
     — no new pan logic needed, scaling via CSS transform already
     makes getBoundingClientRect() (and therefore getPos()) report
     the correct scaled rect automatically. */
  const [canvasZoom, setCanvasZoom] = useState(1);
  const eraserCursorRef = useRef(null);

  /* ===== pen & pencil stroke ===== */
  const [penStroke, setPenStroke] = useState(3);
  const [pencilStroke, setPencilStroke] = useState(2);

  /* COLORS */
  const [penColor, setPenColor] = useState("#111827");
  const [pencilColor, setPencilColor] = useState("#4b5563");
  
  const [typed, setTyped] = useState({

  complaints: "",
  diagnosis: "",
  advice: "",
  notes: "",
  /* ===== NEW (optional, additive) ===== */
  allergies: "",
  investigations: "",
  patientAdvice: "",
  generalExamination: {
    appetite: "", bowel: "", urination: "", sleep: "",
    bp: "", pulse: "", sweating: "", thirst: ""
  }
});
const emptyGeneralExam = () => ({
  appetite: "", bowel: "", urination: "", sleep: "",
  bp: "", pulse: "", sweating: "", thirst: ""
});
  /* Optional-section checkboxes (UI-only; auto-checked on load if content exists) */
  const [showAllergies, setShowAllergies] = useState(false);
  const [showInvestigations, setShowInvestigations] = useState(false);
  /* Which medicine row's name field is currently focused — drives the
     per-row Medicine Suggestion bubble so each row's search starts
     fresh instead of sharing one global query. */
  const [activeMedicineRowId, setActiveMedicineRowId] = useState(null);
const typedDataRef = useRef(typed);  
useEffect(()=>{
  typedDataRef.current = typed;
},[typed]);

  /* ===== STRUCTURED ADVICE / RX MEDICINES ===== */
  const [medicines, setMedicines] = useState([emptyMedicineRow()]);
  const [legacyAdviceHtml, setLegacyAdviceHtml] = useState("");
  const adviceHydratedRef = useRef(false); // avoid clobbering freshly-loaded advice on first render

  // Whenever the structured rows (or preserved legacy text) change,
  // regenerate typed.advice — this is the ONLY place typed.advice
  // is written to from the structured form, keeping the backend
  // contract (typed.advice: String) completely unchanged.
  useEffect(() => {
    if (!adviceHydratedRef.current) return; // don't fire before hydration on load
    const html = buildAdviceHtml(medicines, legacyAdviceHtml);
    setTyped(prev => (prev.advice === html ? prev : { ...prev, advice: html }));
    setDraftTick(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicines, legacyAdviceHtml]);

  const addMedicineRow = () => {
    setMedicines(prev => [...prev, emptyMedicineRow()]);
  };
  const removeMedicineRow = (id) => {
    setMedicines(prev => (prev.length <= 1 ? prev : prev.filter(m => m.id !== id)));
  };
  const updateMedicineField = (id, field, value) => {
    setMedicines(prev => prev.map(m => (m.id === id ? { ...m, [field]: value } : m)));
  };
  const updateGeneralExam = (field, value) => {
    setTyped(prev => ({
      ...prev,
      generalExamination: { ...(prev.generalExamination || emptyGeneralExam()), [field]: value }
    }));
    setDraftTick(Date.now());
  };
  const [savedRxCode, setSavedRxCode] = useState(null);
  const [showFollowUp,    setShowFollowUp]    = useState(false); // NEW
  const [followUpSaving,  setFollowUpSaving]  = useState(false); // NEW
  const [pendingNavAfterFollowUp, setPendingNavAfterFollowUp] = useState(false); // NEW

  /* ===== PRESCRIPTION HISTORY PANEL ===== */
  const [showHistory,      setShowHistory]      = useState(false);
  const [rxHistory,        setRxHistory]        = useState([]);
  const [historyLoading,   setHistoryLoading]   = useState(false);
  const [selectedRxIds,    setSelectedRxIds]    = useState([]);
  const [expandedRxId,     setExpandedRxId]     = useState(null);
   /* ================= LOAD EXISTING RX ON RELOAD ================= */
useEffect(() => {

  if (!rxId) {
    // brand-new prescription — nothing to hydrate from, so allow the
    // structured-advice regeneration effect to run immediately
    adviceHydratedRef.current = true;
    return;
  }

  api.get(`/prescriptions/${rxId}`).then(res => {

    const rx = res.data;

    setSavedRxCode(rx.prescriptionId);   // ⭐ MOST IMPORTANT
    lastSavedRef.current = JSON.stringify({
  typed: res.data.typed,
  pages: res.data.handwritten?.pages || []
});
    if (rx?.typed) {
      setTyped({
        complaints: rx.typed.complaints || "",
        diagnosis: rx.typed.diagnosis || "",
        advice: rx.typed.advice || "",
        notes: rx.typed.notes || "",
        allergies: rx.typed.allergies || "",
        investigations: rx.typed.investigations || "",
        patientAdvice: rx.typed.patientAdvice || "",
        generalExamination: { ...emptyGeneralExam(), ...(rx.typed.generalExamination || {}) }
      });
      setShowAllergies(!!(rx.typed.allergies || "").trim());
      setShowInvestigations(!!(rx.typed.investigations || "").trim());

      const { medicines: loadedMeds, legacyHtml } = parseAdviceHtml(rx.typed.advice || "");
      setMedicines(loadedMeds);
      setLegacyAdviceHtml(legacyHtml);
    }
    // allow the advice-regeneration effect to run for subsequent edits
    setTimeout(() => { adviceHydratedRef.current = true; }, 0);

    if (Array.isArray(rx?.handwritten?.pages)) {
      pagesRef.current = rx.handwritten.pages;
      setPageIndex(0);

      setTimeout(() => redraw(), 50);
    }

  });

}, [rxId]);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [printMode, setPrintMode] = useState("both");

  /* TOOLBAR */
const [toolbarOpen, setToolbarOpen] = useState(false);


 const COLUMN_RATIO = settings?.columnRatio ?? 30;
 const getEditorStyle = (box) => {
  return settings?.typedEditor?.[box] || {
    style: {
      fontFamily: "Times New Roman",
      fontSize: 16,
      lineHeight: 1.5,
      paragraphSpacing: 8,
      textAlign: "left"
    },
    tools: {}
  };
};


  const autoGrow = (e) => {
  e.target.style.height = "auto";
  e.target.style.height = e.target.scrollHeight + "px";
};


  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const liveCanvasRef = useRef(null);
  const liveCtxRef = useRef(null);
  const typedRef = useRef(null);


  const drawingRef = useRef(false);
  const currentStrokeRef = useRef(null);

  const activePointerIdRef = useRef(null);
  const rafRef = useRef(null);
  const latestPointRef = useRef(null);
  // ===== GOLD STORAGE (NO REACT LAG) =====
const pagesRef = useRef([[]]);      // real stroke storage
const redoRef = useRef([]);


const pageIndexRef = useRef(0);
// ensures redraw works after loading old rx
const loadedOnceRef = useRef(false)
useEffect(() => {
  pageIndexRef.current = pageIndex;
}, [pageIndex]);
/* ===== FOLLOWUP LOAD (TEMP FIX) ===== */
useEffect(() => {

  if (visitType !== "followup") return;
  if (!parentRxId) return;

  setSavedRxCode(null);

  api.get(`/prescriptions/${parentRxId}`)
  .then(res => {

    const old = res.data;

    if (old?.typed) {
      setTyped({
        complaints: old.typed.complaints || "",
        diagnosis: old.typed.diagnosis || "",
        advice: "",
        notes: "",
        allergies: old.typed.allergies || "", // carried forward — still known allergies
        investigations: "",
        patientAdvice: "",
        generalExamination: emptyGeneralExam()
      });
      setShowAllergies(!!(old.typed.allergies || "").trim());
      setShowInvestigations(false);
      // fresh follow-up visit → start with a clean medicine list
      setMedicines([emptyMedicineRow()]);
      setLegacyAdviceHtml("");
      adviceHydratedRef.current = true;
    }

  });

}, [visitType, parentRxId]);

  /* ===== smoothing ===== */
  const smoothPointRef = useRef(null);
  const smoothWidthRef = useRef(null);

 const toolboxRef = useRef(null);

const [toolboxPos, setToolboxPos] = useState({ x: 20, y: 120 });
// auto vertical when near left edge
const isVertical = toolboxPos.x < 40;


const dragRef = useRef({
  dragging: false,
  startX: 0,
  startY: 0,
  startLeft: 0,
  startTop: 0
});
const dragRafRef = useRef(null);
useEffect(()=>{

  const wakeUp = ()=>{
    setDraftTick(Date.now());
  };

  window.addEventListener("focus",wakeUp);
  return ()=>window.removeEventListener("focus",wakeUp);

},[]);

  /* ================= LOAD ================= */
useEffect(() => {

  let draftRestored = false;

  const draft = localStorage.getItem(draftKey);

  if (draft) {
    try {
      const parsed = JSON.parse(draft);

      if (
        parsed.patientId === patientId &&
        visitType === "new"
      ) {

       if (parsed.settingsSnapshot) {
  const snap = parsed.settingsSnapshot;

  setSettings(prev => ({
    ...prev,
    ...snap,
    headerBox: {
      ...prev.headerBox,
      ...(snap.headerBox || {}),
      style: { ...prev.headerBox?.style, ...(snap.headerBox?.style || {}) },
      tools: { ...prev.headerBox?.tools, ...(snap.headerBox?.tools || {}) }
    },
    footerBox: {
      ...prev.footerBox,
      ...(snap.footerBox || {}),
      style: { ...prev.footerBox?.style, ...(snap.footerBox?.style || {}) },
      tools: { ...prev.footerBox?.tools, ...(snap.footerBox?.tools || {}) }
    },
    typedEditor: {
      complaints: {
        style: {
          ...prev.typedEditor.complaints.style,
          ...(snap.typedEditor?.complaints?.style || {})
        },
        tools: {
          ...prev.typedEditor.complaints.tools,
          ...(snap.typedEditor?.complaints?.tools || {})
        }
      },
      diagnosis: {
        style: {
          ...prev.typedEditor.diagnosis.style,
          ...(snap.typedEditor?.diagnosis?.style || {})
        },
        tools: {
          ...prev.typedEditor.diagnosis.tools,
          ...(snap.typedEditor?.diagnosis?.tools || {})
        }
      },
      advice: {
        style: {
          ...prev.typedEditor.advice.style,
          ...(snap.typedEditor?.advice?.style || {})
        },
        tools: {
          ...prev.typedEditor.advice.tools,
          ...(snap.typedEditor?.advice?.tools || {})
        }
      },
      notes: {
        style: {
          ...prev.typedEditor.notes.style,
          ...(snap.typedEditor?.notes?.style || {})
        },
        tools: {
          ...prev.typedEditor.notes.tools,
          ...(snap.typedEditor?.notes?.tools || {})
        }
      }
    }
  }));
}

        if (parsed.typed) {
          setTyped(parsed.typed);
          typedDataRef.current = parsed.typed; // ⭐ FIX
        }

        if (Array.isArray(parsed.pages)) {

          pagesRef.current = parsed.pages;

          const restoredIndex = parsed.pageIndex || 0;
          pageIndexRef.current = restoredIndex;
          setPageIndex(restoredIndex);

          requestAnimationFrame(() => redraw());
        }

        // ⭐ initialize lastSaved snapshot
        lastSavedRef.current = JSON.stringify({
          typed: parsed.typed,
          pages: parsed.pages
        });

        draftRestored = true;
      }

    } catch {}
  }

  // ⭐ FIX: this used to run ONLY `if (!draftRestored)`, meaning once a
  // localStorage draft existed for a patient, the doctor's actual saved
  // Prescription Settings (header text, footer text, show/hide toggles,
  // fonts, etc.) were never re-fetched again — the pad silently kept
  // using whatever settings snapshot happened to be cached inside that
  // old draft. So editing Prescription Settings (e.g. typing header /
  // footer text and clicking "Make Default") appeared to do nothing for
  // any patient who already had a draft. A draft should only restore
  // in-progress TEXT/HANDWRITING for that patient — it must never
  // permanently pin a stale copy of the doctor's global print settings.
  // Always re-fetch the latest settings from the API; they're merged in
  // on top of (and therefore take priority over) whatever the draft
  // snapshot set a moment earlier.
  api.get("/doctor/prescription-settings")
    .then(res => {
      const parsed = res.data;
      if (!parsed) return;
      setSettings(prev => ({
        ...prev,
        ...parsed,
        headerBox: {
          ...prev.headerBox,
          ...(parsed.headerBox || {}),
          style: { ...prev.headerBox?.style, ...(parsed.headerBox?.style || {}) },
          tools: { ...prev.headerBox?.tools, ...(parsed.headerBox?.tools || {}) }
        },
        footerBox: {
          ...prev.footerBox,
          ...(parsed.footerBox || {}),
          style: { ...prev.footerBox?.style, ...(parsed.footerBox?.style || {}) },
          tools: { ...prev.footerBox?.tools, ...(parsed.footerBox?.tools || {}) }
        },
        typedEditor: {
          complaints: {
            style: { ...prev.typedEditor.complaints.style, ...(parsed.typedEditor?.complaints?.style || {}) },
            tools: { ...prev.typedEditor.complaints.tools, ...(parsed.typedEditor?.complaints?.tools || {}) }
          },
          diagnosis: {
            style: { ...prev.typedEditor.diagnosis.style, ...(parsed.typedEditor?.diagnosis?.style || {}) },
            tools: { ...prev.typedEditor.diagnosis.tools, ...(parsed.typedEditor?.diagnosis?.tools || {}) }
          },
          advice: {
            style: { ...prev.typedEditor.advice.style, ...(parsed.typedEditor?.advice?.style || {}) },
            tools: { ...prev.typedEditor.advice.tools, ...(parsed.typedEditor?.advice?.tools || {}) }
          },
          notes: {
            style: { ...prev.typedEditor.notes.style, ...(parsed.typedEditor?.notes?.style || {}) },
            tools: { ...prev.typedEditor.notes.tools, ...(parsed.typedEditor?.notes?.tools || {}) }
          }
        }
      }));
      if (parsed.penColor) setPenColor(parsed.penColor);
    })
    .catch(() => {});


  api.get(`/patients/${patientId}`)
    .then(async r => {

      setPatient(r.data);

      if (!rxId) {

        const rxRes = await api.get(`/prescriptions/${patientId}`);

        const newRxId = rxRes.data.prescriptionId;

        setSavedRxCode(newRxId);

        navigate(
          `/staff-portal/prescription/${patientId}/${newRxId}?visitType=${visitType}`,
          { replace: true }
        );
      }

    })
    .catch(err => {
      console.log("PATIENT LOAD ERROR:", err?.response?.data);
    });

}, [patientId, rxId]);
/* ===== START AUTOSAVE ONLY AFTER RX EXISTS ===== */
useEffect(()=>{

  if(!savedRxCode) return;
  if(creatingRef.current) return;

  if(autosaveTimerRef.current){
    clearInterval(autosaveTimerRef.current);
  }

  autosaveTimerRef.current = setInterval(()=>{

    if(creatingRef.current) return;
    if(savingRef.current) return;
    if(!savedRxCode) return;

    autoSave();

  },5000);

  return ()=>{
    if(autosaveTimerRef.current){
      clearInterval(autosaveTimerRef.current);
    }
  };

},[savedRxCode]);

 const initCanvas = () => {
  const canvas = canvasRef.current;
  const live = liveCanvasRef.current;
  if (!canvas || !live) return;

  const dpr = window.devicePixelRatio || 1;

  [canvas, live].forEach(c => {
    c.width = DRAW_WIDTH * dpr;
    c.height = DRAW_HEIGHT * dpr;
    c.style.width = `${DRAW_WIDTH}px`;
    c.style.height = `${DRAW_HEIGHT}px`;
  });

  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const liveCtx = live.getContext("2d");
  liveCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  liveCtx.lineCap = "round";
  liveCtx.lineJoin = "round";

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, DRAW_WIDTH, DRAW_HEIGHT);

  ctxRef.current = ctx;
  liveCtxRef.current = liveCtx;

  redraw();
};

useEffect(() => {

  if (!settings) return;
  if (mode !== "handwritten") return;

  initCanvas();

}, [mode, settings]);

/* ================= ZOOM — direct size-based (robust) =================
   initCanvas() above imperatively hard-sets canvas.style.width/height
   to a fixed DRAW_WIDTH/DRAW_HEIGHT px AFTER React renders, which wins
   over any JSX-level width. A CSS `transform: scale()` on an ancestor
   technically still applies over that fixed size, but to avoid any
   ambiguity around overflow/scroll-region edge cases interacting with
   that fixed-px sizing quirk, zoom instead directly resizes the two
   canvases' CSS display size (not their internal resolution — that
   stays untouched, so drawing crispness is unaffected). This also
   guarantees the parent's `overflow:auto` scroll region reflows
   correctly, since it's a real layout-size change, not just paint.
   Placed AFTER the initCanvas effect so it always runs later and wins
   whenever both fire together (e.g. on mode switch). */
useEffect(() => {
  if (mode !== "handwritten") return;
  const w = Math.round(DRAW_WIDTH * canvasZoom);
  const h = Math.round(DRAW_HEIGHT * canvasZoom);
  [canvasRef.current, liveCanvasRef.current].forEach(c => {
    if (!c) return;
    c.style.width = w + "px";
    c.style.height = h + "px";
  });
}, [canvasZoom, mode, pageIndex, settings]);

  useEffect(() => {

  if (!settings) return;
  if (mode !== "handwritten") return;

  redraw();

}, [settings, mode]);
useEffect(() => {

  if (!settings) return;
  if (mode !== "handwritten") return;

  redraw();
  loadedOnceRef.current = true;

}, [pageIndex, mode, settings]);

/* ================= ERASER CURSOR INDICATOR (NEW) =================
   Shows a circle the size of the current eraser at the pointer's
   position while hovering — pure DOM manipulation via a ref, no
   React state, so it never triggers re-renders and can't add any lag
   to the drawing loop above. Independent effect; doesn't touch any
   existing drawing code. */
useEffect(() => {
  if (mode !== "handwritten") return;
  const el = liveCanvasRef.current;
  const cursorEl = eraserCursorRef.current;
  if (!el || !cursorEl) return;

  const hide = () => { cursorEl.style.display = "none"; };

  const onMove = (e) => {
    if (tool !== "eraser" || e.pointerType === "touch") { hide(); return; }
    const rect = el.getBoundingClientRect();
    if (!rect.width) { hide(); return; }
    const scale = rect.width / DRAW_WIDTH; // reflects zoom automatically
    const diameter = eraserWidth * scale;
    cursorEl.style.display = "block";
    cursorEl.style.width = diameter + "px";
    cursorEl.style.height = diameter + "px";
    cursorEl.style.left = (e.clientX - diameter / 2) + "px";
    cursorEl.style.top = (e.clientY - diameter / 2) + "px";
  };

  el.addEventListener("pointermove", onMove);
  el.addEventListener("pointerleave", hide);
  el.addEventListener("pointerup", onMove);
  return () => {
    el.removeEventListener("pointermove", onMove);
    el.removeEventListener("pointerleave", hide);
    el.removeEventListener("pointerup", onMove);
    hide();
  };
}, [mode, tool, eraserWidth, canvasZoom]);

  useEffect(() => {
  return () => {
    window.removeEventListener("pointermove", onDrag);
    window.removeEventListener("pointerup", endDrag);

    if (dragRafRef.current)
      cancelAnimationFrame(dragRafRef.current);

    if (rafRef.current)
      cancelAnimationFrame(rafRef.current);
  };
}, []);


 /* ================= REDRAW ================= */
const redraw = () => {
  const ctx = ctxRef.current; // BASE layer only
  if (!ctx) return;

  ctx.clearRect(0, 0, DRAW_WIDTH, DRAW_HEIGHT);
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, DRAW_WIDTH, DRAW_HEIGHT);

  const strokes = (
  pagesRef.current[pageIndexRef.current] || []
).filter(s => s && s.points?.length);

  strokes.forEach(s => {
    ctx.save();

    ctx.globalCompositeOperation =
      s.tool === "eraser" ? "destination-out" : "source-over";

    ctx.strokeStyle = s.color || "#111827";
    ctx.beginPath();

    for (let i = 0; i < s.points.length - 1; i++) {
      const p1 = s.points[i];
      const p2 = s.points[i + 1];
      const mx = (p1.x + p2.x) / 2;
      const my = (p1.y + p2.y) / 2;

      ctx.lineWidth =
  s.tool === "pen"
    ? s.width
    : (p2.w || s.width);

      if (i === 0) ctx.moveTo(p1.x, p1.y);
      ctx.quadraticCurveTo(p1.x, p1.y, mx, my);
    }

    ctx.stroke();
    ctx.restore();
  });

  // column divider
  const x = (DRAW_WIDTH * COLUMN_RATIO) / 100;
  ctx.save();
  ctx.setLineDash([6, 6]);
  ctx.strokeStyle = "#d1d5db";
  ctx.beginPath();
  ctx.moveTo(x, 0);
  ctx.lineTo(x, DRAW_HEIGHT);
  ctx.stroke();
  ctx.restore();
};


  /* ================= POSITION ================= */
  const getPos = e => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (DRAW_WIDTH / r.width),
      y: (e.clientY - r.top) * (DRAW_HEIGHT / r.height)
    };
  };

  /* ===== TOOL STROKE ===== */
const getToolStroke = (pressure = 0.7) => {
  if (tool === "pen") return penStroke * (0.5 + pressure);
  if (tool === "pencil") return pencilStroke * 0.7;
  return eraserWidth;
};

/* ===== STROKE OPTIMIZATION (GOLD) ===== */
const simplifyStroke = (stroke, tolerance = 1.2) => {
  if (!stroke?.points || stroke.points.length < 3) return stroke;

  const points = stroke.points;
  const simplified = [points[0]];

  for (let i = 1; i < points.length - 1; i++) {
    const prev = simplified[simplified.length - 1];
    const curr = points[i];
    const next = points[i + 1];

    const dx = next.x - prev.x;
    const dy = next.y - prev.y;

    const mag = Math.sqrt(dx * dx + dy * dy) || 1;

    const dist =
      Math.abs(
        dy * curr.x -
        dx * curr.y +
        next.x * prev.y -
        next.y * prev.x
      ) / mag;

    if (dist > tolerance) {
      simplified.push(curr);
    }
  }

  simplified.push(points[points.length - 1]);

  return {
    ...stroke,
    points: simplified
  };
};

 /* ================= POINTER DOWN ================= */
const pointerDown = e => {

  // 🚫 Finger NEVER draws
  if (e.pointerType !== "pen") return;

  if (activePointerIdRef.current !== null) return;

  activePointerIdRef.current = e.pointerId;
  drawingRef.current = true;

  if (liveCanvasRef.current?.setPointerCapture) {
  liveCanvasRef.current.setPointerCapture(e.pointerId);
}

  const { x, y } = getPos(e);

  smoothPointRef.current = { x, y };
  smoothWidthRef.current =
  tool === "pen"
    ? penStroke
    : getToolStroke(e.pressure || 0.7);

  currentStrokeRef.current = {
    points: [{ x, y, w: smoothWidthRef.current }],
    width:
  tool === "pen"
    ? penStroke
    : tool === "pencil"
    ? pencilStroke
    : eraserWidth,

    color:
      tool === "pen"
        ? penColor
        : tool === "pencil"
        ? pencilColor
        : "#000000",
    tool
  };
  // ⭐ INSTANT START DOT (fixes non-cursive lag)
const ctx = liveCtxRef.current;
if (ctx) {
  ctx.save();

  ctx.globalCompositeOperation =
    tool === "eraser"
      ? "destination-out"
      : "source-over";

  ctx.fillStyle =
    tool === "pen"
      ? penColor
      : tool === "pencil"
      ? pencilColor
      : "#000";

  const r = smoothWidthRef.current / 2;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}
};

  /* ===== SMOOTH HELPERS ===== */
  /* ===== SMOOTH HELPERS (AUTO CURSIVE DETECT) ===== */
const getSmoothedPoint = (x, y) => {
  if (!smoothPointRef.current) {
    smoothPointRef.current = { x, y, t: performance.now() };
    return { x, y };
  }

  const prev = smoothPointRef.current;

  const dx = x - prev.x;
  const dy = y - prev.y;
  const dist = Math.hypot(dx, dy);

  const now = performance.now();
  const dt = Math.max(now - (prev.t || now), 1);
  const speed = dist / dt;

  /*
    ⭐ MEDICAL WRITING LOGIC
    --------------------------------
    very short movement  -> NO smoothing
    normal letters       -> light smoothing
    fast cursive         -> strong smoothing
  */

  let alpha;

// continuous blend (NO hard jumps)
if (dist < 2) {
  alpha = 0.9;
} else if (dist < 6) {
  alpha = 0.7;
} else if (speed > 0.35) {
  alpha = 0.8;
} else {
  alpha = 0.6;
}


  const sx = prev.x + dx * alpha;
  const sy = prev.y + dy * alpha;

  smoothPointRef.current = { x: sx, y: sy, t: now };

  return { x: sx, y: sy };
};


  const getSmoothedWidth = w => {
    if (smoothWidthRef.current === null) {
      smoothWidthRef.current = w;
      return w;
    }

    const alpha = 0.25;

    const sw =
      smoothWidthRef.current +
      (w - smoothWidthRef.current) * alpha;

    smoothWidthRef.current = sw;
    return sw;
  };
 /* ================= TOOLBOX DRAG (STABLE) ================= */

const startDrag = (e) => {
  // ONLY finger drags toolbox
  if (e.pointerType !== "touch") return;

  e.preventDefault();
  e.stopPropagation();

  dragRef.current.dragging = true;
  dragRef.current.startX = e.clientX;
  dragRef.current.startY = e.clientY;
  dragRef.current.startLeft = toolboxPos.x;
  dragRef.current.startTop = toolboxPos.y;

  e.currentTarget.setPointerCapture?.(e.pointerId);

  window.addEventListener("pointermove", onDrag);
  window.addEventListener("pointerup", endDrag);
};

const onDrag = (e) => {
  if (!dragRef.current.dragging) return;

  const dx = e.clientX - dragRef.current.startX;
  const dy = e.clientY - dragRef.current.startY;

  if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);

  dragRafRef.current = requestAnimationFrame(() => {
    setToolboxPos({
      x: dragRef.current.startLeft + dx,
      y: dragRef.current.startTop + dy
    });
  });
};

const endDrag = () => {
  dragRef.current.dragging = false;

  window.removeEventListener("pointermove", onDrag);
  window.removeEventListener("pointerup", endDrag);
};

const getVelocityWidth = (p1, p2) => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const dist = Math.hypot(dx, dy);

  const dt = Math.max((p2.t || 0) - (p1.t || 0), 1);
  const velocity = dist / dt;

  const maxW = tool === "pen" ? penStroke * 2 : pencilStroke;
  const minW = 0.6;

  return Math.max(minW, maxW - velocity * 0.25);
};

/* ===== LIVE DRAW (FIXED STEP 9) ===== */
const drawLiveStroke = () => {
  const ctx = liveCtxRef.current;
  const s = currentStrokeRef.current;
  if (!ctx || !s) return;

  const pts = s.points;
  if (pts.length < 3) return;

  const p1 = pts[pts.length - 3];
  const p2 = pts[pts.length - 2];
  const p3 = pts[pts.length - 1];

  const midX1 = (p1.x + p2.x) / 2;
  const midY1 = (p1.y + p2.y) / 2;
  const midX2 = (p2.x + p3.x) / 2;
  const midY2 = (p2.y + p3.y) / 2;

  ctx.save();

  ctx.globalCompositeOperation =
    s.tool === "eraser"
      ? "destination-out"
      : "source-over";

  ctx.strokeStyle = s.color;
  ctx.lineWidth =
  s.tool === "pen"
    ? s.width
    : (p2.w || s.width);

  ctx.beginPath();
  ctx.moveTo(midX1, midY1);
  ctx.quadraticCurveTo(p2.x, p2.y, midX2, midY2);
  ctx.stroke();

  ctx.restore();
};

/* ===== DRAW ONE STROKE DIRECTLY TO BASE ===== */
const drawStrokeToBase = (stroke) => {
  const ctx = ctxRef.current;
  if (!ctx || !stroke?.points?.length) return;

  ctx.save();

  ctx.globalCompositeOperation =
    stroke.tool === "eraser"
      ? "destination-out"
      : "source-over";

  ctx.strokeStyle = stroke.color || "#111827";
  ctx.beginPath();

  const pts = stroke.points;

  for (let i = 0; i < pts.length - 1; i++) {
    const p1 = pts[i];
    const p2 = pts[i + 1];

    const mx = (p1.x + p2.x) / 2;
    const my = (p1.y + p2.y) / 2;

    ctx.lineWidth =
  stroke.tool === "pen"
    ? stroke.width
    : (p2.w || stroke.width);

    if (i === 0) ctx.moveTo(p1.x, p1.y);
    ctx.quadraticCurveTo(p1.x, p1.y, mx, my);
  }

  ctx.stroke();
  ctx.restore();
};


/* ===== POINTER MOVE (ZERO-LAG FIX) ===== */

const processDraw = (ev) => {
  if (!currentStrokeRef.current) return;

  // RAW pen position (IMPORTANT)
let { x: rawX, y: rawY } = getPos(ev);

// smooth ONLY for drawing
const smoothed = getSmoothedPoint(rawX, rawY);
let x = smoothed.x;
let y = smoothed.y;

  const pressure =
    ev.pointerType === "pen"
      ? (ev.pressure || 0.5)
      : 0.7;

  const pts = currentStrokeRef.current.points;
  const last = pts[pts.length - 1];

  let w = getToolStroke(pressure);

if (last && tool !== "eraser") {

  // PEN = constant clean medical writing
  if (tool === "pen") {
  // constant thickness = medical writing stability
  w = penStroke;
}


  // PENCIL = natural feel (optional dynamics)
  else if (tool === "pencil") {
    w = getVelocityWidth(last, { x, y, t: ev.t });
  }
}
  // NO WIDTH SMOOTHING (causes bold/unbold issues)

  // ⭐ GOLD PERFORMANCE PATCH (POINT THINNING)
  if (last) {
    const dx = rawX - last.x;
const dy = rawY - last.y;


    // skip very tiny movement
    if (dx * dx + dy * dy < 2.5) return;
  }

  pts.push({ x, y, w, t: ev.t });

  drawLiveStroke();
};

const drawLoop = () => {
  rafRef.current = null;

  const ev = latestPointRef.current;
  if (!ev) return;

  processDraw(ev);

  if (drawingRef.current) {
    rafRef.current = requestAnimationFrame(drawLoop);
  }
};

const pointerMove = e => {
  if (e.pointerType !== "pen") return;
  if (e.pointerId !== activePointerIdRef.current) return;
  if (!drawingRef.current) return;

  const ev = {
    clientX: e.clientX,
    clientY: e.clientY,
    pressure: e.pressure,
    pointerType: e.pointerType,
    t: e.timeStamp
  };

  latestPointRef.current = ev;

  // ⭐ instant draw = NO lag
 if (!rafRef.current) {
  processDraw(ev);  // ⭐ REQUIRED
  rafRef.current = requestAnimationFrame(drawLoop);
}
};

const pointerUp = e => {
  if (e.pointerType !== "pen") return;
  if (e.pointerId !== activePointerIdRef.current) return;

  if (rafRef.current) {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }

  if (liveCanvasRef.current?.releasePointerCapture) {
    liveCanvasRef.current.releasePointerCapture(e.pointerId);
  }

  drawingRef.current = false;
  activePointerIdRef.current = null;

  let finishedStroke = currentStrokeRef.current;
  if (!finishedStroke) return;

  // SAFE COPY
  finishedStroke = {
    ...finishedStroke,
    points: [...finishedStroke.points]
  };

  /* ⭐ FORCE FINAL RAW ENDPOINT FIRST */
  const finalPos = getPos(e);
  finishedStroke.points.push({
    x: finalPos.x,
    y: finalPos.y,
    w: finishedStroke.width,
    t: e.timeStamp
  });

  /* ⭐ simplify only non-pen */
  if (finishedStroke.tool !== "pen") {
    finishedStroke = simplifyStroke(finishedStroke);
  }

  // store
  if (!pagesRef.current[pageIndexRef.current]) {
    pagesRef.current[pageIndexRef.current] = [];
  }

  pagesRef.current[pageIndexRef.current].push(finishedStroke);

  // draw final stroke
  drawStrokeToBase(finishedStroke);

  // clear live layer
  liveCtxRef.current?.clearRect(0, 0, DRAW_WIDTH, DRAW_HEIGHT);

  redoRef.current = [];
  lastEraseRef.current = null; // a fresh stroke invalidates the pending object-erase undo

  // reset
  smoothPointRef.current = null;
  smoothWidthRef.current = null;
  currentStrokeRef.current = null;
  latestPointRef.current = null;
  setDraftTick(Date.now());
};

/* ===== POINTER CANCEL (ZERO-LAG SAFE) ===== */
const pointerCancel = e => {

  // 🚫 ignore non-pen
  if (e.pointerType !== "pen") return;

  drawingRef.current = false;
  activePointerIdRef.current = null;

  // release pointer safely
  if (liveCanvasRef.current?.releasePointerCapture) {
    liveCanvasRef.current.releasePointerCapture(e.pointerId);
  }

  // clear live preview layer
  liveCtxRef.current?.clearRect(0, 0, DRAW_WIDTH, DRAW_HEIGHT);

  // reset smoothing + stroke refs
  smoothPointRef.current = null;
  smoothWidthRef.current = null;
  currentStrokeRef.current = null;
  latestPointRef.current = null;

  // stop pending frame
  if (rafRef.current) {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }
};

/* ================= STROKE / OBJECT ERASER (NEW) =================
   Separate, self-contained code path — completely independent of the
   pixel-eraser / pen drawing logic above, so none of the delicate
   smoothing/pressure code is touched. Deletes a WHOLE stroke when the
   pointer passes near any of its points, instead of erasing pixels. */
const eraseStrokeAt = (x, y) => {
  const page = pagesRef.current[pageIndexRef.current];
  if (!page || !page.length) return;

  const hitRadius = Math.max(eraserWidth, 16);
  const hitRadiusSq = hitRadius * hitRadius;

  for (let i = page.length - 1; i >= 0; i--) { // topmost (most recent) stroke first
    const s = page[i];
    if (!s?.points?.length) continue;
    const hit = s.points.some(p => {
      const dx = p.x - x, dy = p.y - y;
      return dx * dx + dy * dy <= hitRadiusSq;
    });
    if (hit) {
      const [removed] = page.splice(i, 1);
      lastEraseRef.current = { index: i, stroke: removed, page: pageIndexRef.current };
      redraw();
      setDraftTick(Date.now());
      return; // one stroke per hit — avoids accidentally wiping several at once
    }
  }
};

const strokeErasePointerDown = e => {
  if (e.pointerType !== "pen") return; // same pen-only rule as every other draw tool
  if (activePointerIdRef.current !== null) return;
  activePointerIdRef.current = e.pointerId;
  strokeErasingRef.current = true;
  if (liveCanvasRef.current?.setPointerCapture) {
    liveCanvasRef.current.setPointerCapture(e.pointerId);
  }
  const { x, y } = getPos(e);
  eraseStrokeAt(x, y);
};

const strokeErasePointerMove = e => {
  if (!strokeErasingRef.current) return;
  if (e.pointerId !== activePointerIdRef.current) return;
  const { x, y } = getPos(e);
  eraseStrokeAt(x, y);
};

const strokeErasePointerUp = e => {
  strokeErasingRef.current = false;
  if (liveCanvasRef.current?.releasePointerCapture && e.pointerId != null) {
    liveCanvasRef.current.releasePointerCapture(e.pointerId);
  }
  activePointerIdRef.current = null;
};

/* ===== ADD PAGE (SAFE VERSION) ===== */
const addPage = () => {

  // ⭐⭐⭐ COMMIT ACTIVE STROKE FIRST ⭐⭐⭐
  if (currentStrokeRef.current?.points?.length) {

    let finishedStroke = {
      ...currentStrokeRef.current,
      points: [...currentStrokeRef.current.points]
    };

    if (!pagesRef.current[pageIndexRef.current]) {
      pagesRef.current[pageIndexRef.current] = [];
    }

    pagesRef.current[pageIndexRef.current].push(finishedStroke);

    drawStrokeToBase(finishedStroke);
  }

  // stop drawing safely
  drawingRef.current = false;
  currentStrokeRef.current = null;
  latestPointRef.current = null;

  liveCtxRef.current?.clearRect(0,0,DRAW_WIDTH,DRAW_HEIGHT);

  // ⭐ NOW create next page
  pagesRef.current.push([]);

  const newIndex = pagesRef.current.length - 1;

  pageIndexRef.current = newIndex;
  setPageIndex(newIndex);

  requestAnimationFrame(()=>{
    redraw();
  });
};

const goToPage = (index) => {
  if (index < 0 || index >= pagesRef.current.length) return;

  // stop any active drawing first
  drawingRef.current = false;
  currentStrokeRef.current = null;

  // clear live preview layer
  liveCtxRef.current?.clearRect(0, 0, DRAW_WIDTH, DRAW_HEIGHT);

  // sync ref BEFORE render
  pageIndexRef.current = index;

  // switch UI page
  setPageIndex(index);
  setDraftTick(Date.now());   // ⭐ ADD THIS

  // redraw after state settles
  requestAnimationFrame(() => {
    redraw();
  });
};

  /* ================= UNDO / REDO (GOLD REF VERSION) ================= */

const undo = () => {
  // If the most recent edit was an object-erase (stroke eraser), undo
  // restores that stroke first — kept as its own single-level branch
  // so the original pen undo/redo stack below is untouched.
  if (lastEraseRef.current && lastEraseRef.current.page === pageIndexRef.current) {
    const { index, stroke } = lastEraseRef.current;
    if (!pagesRef.current[pageIndexRef.current]) {
      pagesRef.current[pageIndexRef.current] = [];
    }
    const page = pagesRef.current[pageIndexRef.current];
    page.splice(Math.min(index, page.length), 0, stroke);
    lastEraseRef.current = null;
    redraw();
    setDraftTick(Date.now());
    return;
  }

  const page = pagesRef.current[pageIndexRef.current];
  if (!page || !page.length) return;

  const last = page.pop();
  redoRef.current.push(last);

  redraw();
  setDraftTick(Date.now());   // ⭐ ADD THIS
};
const redo = () => {
  const item = redoRef.current.pop();
  if (!item) return;

  if (!pagesRef.current[pageIndexRef.current]) {
    pagesRef.current[pageIndexRef.current] = [];
  }

  pagesRef.current[pageIndexRef.current].push(item);

  redraw();
  setDraftTick(Date.now());   // ⭐ ADD THIS
};
/* ===== APPLY PARAGRAPH SPACING BEFORE SAVE ===== */
const applyParagraphSpacing = (html, spacing = 8) => {
  if (!html) return "";

  return html.replace(
    /<p>/g,
    `<p style="margin-bottom:${spacing}px;">`
  );
};

const savePrescription = async () => {
  try {
    setSaving(true);

    const vectorPages = pagesRef.current.map(
      page => (page || []).filter(s => s?.points?.length)
    );

   const rawSettings = settings;   // 🔥 USE LIVE FULL STATE

    const payload = {
      patientId,
      typed: {
        complaints: applyParagraphSpacing(
          typed.complaints,
          getEditorStyle("complaints")?.style?.paragraphSpacing
        ),
        diagnosis: applyParagraphSpacing(
          typed.diagnosis,
          getEditorStyle("diagnosis")?.style?.paragraphSpacing
        ),
        advice: applyParagraphSpacing(
          typed.advice,
          getEditorStyle("advice")?.style?.paragraphSpacing
        ),
        notes: applyParagraphSpacing(
          typed.notes,
          getEditorStyle("notes")?.style?.paragraphSpacing
        ),
        patientAdvice: applyParagraphSpacing(
          typed.patientAdvice,
          getEditorStyle("patientAdvice")?.style?.paragraphSpacing
        ),
        // Plain optional fields — checkbox-gated, so blank when hidden
        allergies: showAllergies ? (typed.allergies || "") : "",
        investigations: showInvestigations ? (typed.investigations || "") : "",
        generalExamination: typed.generalExamination || emptyGeneralExam(),
      },
      handwritten: { pages: vectorPages },
      settings: settings,
      modeUsed:
        printMode === "typed"
          ? ["typed"]
          : printMode === "handwritten"
          ? ["handwritten"]
          : ["typed", "handwritten"],
      visitType,
      parentRxId
    };

    if (!savedRxCode) {

      creatingRef.current = true;

      const res = await api.post("/prescriptions", payload);

      const newRx = res.data.prescriptionId;

      setSavedRxCode(newRx);

      navigate(
        `/staff-portal/prescription/${patientId}/${newRx}?visitType=${visitType}`,
        { replace: true }
      );

      creatingRef.current = false;
    } 
    else {
      await api.put(`/prescriptions/${savedRxCode}`, payload);
    }

    // ⭐⭐⭐ CRITICAL FIX ⭐⭐⭐
    lastSavedRef.current = JSON.stringify({
      typed,
      pages: pagesRef.current
    });

  } catch (err) {
    console.log("SAVE ERROR:", err?.response?.data);
  } finally {
    creatingRef.current = false;
    setSaving(false);
  }
};

/* ===== LOAD PRESCRIPTION HISTORY ===== */
const loadHistory = async () => {
  if (rxHistory.length > 0) { setShowHistory(true); return; } // already loaded
  setHistoryLoading(true);
  try {
    const res = await api.get(`/prescriptions/patient/${patientId}`);
    setRxHistory(res.data || []);
    setShowHistory(true);
  } catch (err) {
    console.error("History load error:", err);
  } finally {
    setHistoryLoading(false);
  }
};

const toggleSelectRx = (prescriptionId) => {
  setSelectedRxIds(prev =>
    prev.includes(prescriptionId)
      ? prev.filter(id => id !== prescriptionId)
      : [...prev, prescriptionId]
  );
};

const selectAllRx = () => {
  if (selectedRxIds.length === rxHistory.length) {
    setSelectedRxIds([]);
  } else {
    setSelectedRxIds(rxHistory.map(rx => rx.prescriptionId));
  }
};

const copySelectedToAdvice = () => {
  const selected = rxHistory.filter(rx => selectedRxIds.includes(rx.prescriptionId));
  if (!selected.length) return;
  const combined = selected
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map(rx => {
      const date = new Date(rx.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
      const advice = (rx.typed?.advice || "").replace(/<[^>]+>/g, " ").trim();
      return advice ? `[${date}] ${advice}` : null;
    })
    .filter(Boolean)
    .join("\n\n");
  if (combined) {
    setTyped(prev => ({
      ...prev,
      advice: prev.advice ? prev.advice + "\n\n" + combined : combined
    }));
  }
  setShowHistory(false);
  setSelectedRxIds([]);
};

/* ===== COMPLETE CONSULTATION ===== */
const completeConsultation = async () => {
  try {
    setCompleting(true);

    // Save first
    await savePrescription();

    // Mark as completed
    if (savedRxCode) {
      await api.patch(`/prescriptions/${savedRxCode}/complete`);
    }

    // Clear local draft
    localStorage.removeItem(draftKey);

    // Show follow-up modal instead of navigating immediately
    setPendingNavAfterFollowUp(true);
    setShowFollowUp(true);

  } catch (err) {
    console.error("COMPLETE CONSULT ERROR:", err?.response?.data);
    alert("Failed to complete consultation. Please try again.");
  } finally {
    setCompleting(false);
  }
};

/* ===== FOLLOW-UP HANDLERS ===== */
const handleFollowUpSave = async (date, note) => {
  setFollowUpSaving(true);
  try {
    await api.post(`/patients/${patientId}/followup`, {
      followUpDate: date,
      followUpNote: note,
    });
  } catch (err) {
    console.error("FOLLOW-UP SAVE ERROR:", err?.response?.data);
  } finally {
    setFollowUpSaving(false);
    setShowFollowUp(false);
    if (pendingNavAfterFollowUp) navigate("/staff-portal/dashboard");
  }
};

const handleFollowUpSkip = () => {
  setShowFollowUp(false);
  if (pendingNavAfterFollowUp) navigate("/staff-portal/dashboard");
};

/* ===== AUTO SAVE ===== */
const autoSave = async () => {

  const snapshot = JSON.stringify({
    typed: typedDataRef.current,
    pages: pagesRef.current
  });

  // Always update local draft
  localStorage.setItem(
    draftKey,
    JSON.stringify({
      patientId,
      typed: typedDataRef.current,
      pages: pagesRef.current,
      pageIndex: pageIndexRef.current,
      settingsSnapshot: settings
    })
  );

  if (creatingRef.current) return;
  if (!savedRxCode) return;
  if (savingRef.current) return;
  if (snapshot === lastSavedRef.current) return;

  try {

    savingRef.current = true;

    await api.put(`/prescriptions/${savedRxCode}`, {
      patientId,
      typed: typedDataRef.current,
      handwritten: { pages: pagesRef.current },
      settings,
      modeUsed:["typed","handwritten"],
      visitType,
      parentRxId
    });

    lastSavedRef.current = snapshot;

    autoSaveFailRef.current = 0;
    setShowSaveWarn(false);
    console.log("✅ AUTOSAVED");

  } catch (err) {
    autoSaveFailRef.current += 1;
    if (autoSaveFailRef.current >= 2) setShowSaveWarn(true);
    console.log("❌ AUTOSAVE FAILED", err?.response?.data);
  } finally {
    savingRef.current = false;
  }
};

/* ===== PRINT SAFE HTML (TIPTAP FIX) ===== */
const sanitizePrintHTML = (html = "") => {
  return html
    .replace(/<\/p>\s*<p>/g, "<br/>")
    .replace(/<p>/g, "")
    .replace(/<\/p>/g, "");
};

  if (!settings || !patient) {
    return (
      <div style={{
        minHeight: "60vh",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 18, fontFamily: "'Inter',sans-serif",
      }}>
        <style>{`
          @keyframes rx-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%       { opacity: 0.45; transform: scale(0.92); }
          }
        `}</style>
        <div style={{
          width: 52, height: 52, borderRadius: "50%",
          background: "linear-gradient(135deg,#4A7C59,#2D5A3D)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 24,
          animation: "rx-pulse 1.5s ease-in-out infinite",
        }}>&#x1F4CB;</div>
        <div style={{ textAlign: "center" }}>
          <div style={{
            fontSize: 15, fontWeight: 700, color: "#2C1810",
            marginBottom: 6,
          }}>
            Loading prescription pad…
          </div>
          <div style={{
            fontSize: 13, color: "#9A7B60", fontFamily: "monospace",
            background: "#F5EDD8", padding: "3px 12px",
            borderRadius: 8, display: "inline-block",
          }}>
            {patientId}
          </div>
        </div>
      </div>
    );
  }

  
  return (
    <>
    {/* —— Autosave warning banner —— */}
    {showSaveWarn && (
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 99999,
        background: "linear-gradient(135deg,#FFFBEB,#FEF3C7)",
        borderBottom: "2px solid #F59E0B",
        padding: "10px 20px",
        display: "flex", alignItems: "center", gap: 12,
        boxShadow: "0 2px 12px rgba(245,158,11,0.18)",
        fontFamily: "'Inter',sans-serif",
      }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#92400E" }}>
          Auto-save is failing — check your connection. Your work is saved locally.
        </span>
        <button
          onClick={() => setShowSaveWarn(false)}
          style={{
            background: "transparent", border: "1px solid #F59E0B",
            borderRadius: 7, padding: "3px 12px",
            color: "#B45309", fontSize: 12, fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Dismiss
        </button>
      </div>
    )}

    {/* Follow-up Modal — shown after consultation is completed */}
    {showFollowUp && patient && (
      <FollowUpModal
        patientName={patient.name}
        patientId={patient.patientId}
        saving={followUpSaving}
        onSave={handleFollowUpSave}
        onSkip={handleFollowUpSkip}
      />
    )}

    {/* ── PRESCRIPTION HISTORY PANEL ── */}
    {showHistory && (
      <div style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "flex-start", justifyContent: "flex-end",
        padding: "0",
      }} onClick={(e) => { if (e.target === e.currentTarget) { setShowHistory(false); setSelectedRxIds([]); } }}>
        <div style={{
          width: "min(480px, 95vw)", height: "100vh",
          background: "#fff", boxShadow: "-4px 0 30px rgba(0,0,0,0.15)",
          display: "flex", flexDirection: "column",
          fontFamily: "'Inter','Segoe UI',sans-serif",
          animation: "slideInRight 0.2s ease",
        }}>
          {/* Header */}
          <div style={{ padding: "18px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#0F172A" }}>📋 Prescription History</h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748B" }}>{patient?.name} · {rxHistory.length} prescription{rxHistory.length !== 1 ? "s" : ""}</p>
            </div>
            <button onClick={() => { setShowHistory(false); setSelectedRxIds([]); }}
              style={{ border: "none", background: "#F1F5F9", borderRadius: 8, width: 32, height: 32, fontSize: 16, cursor: "pointer", color: "#64748B" }}>✕</button>
          </div>

          {/* Select all + Copy button */}
          {rxHistory.length > 0 && (
            <div style={{ padding: "10px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: "#FAFAFA" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#374151" }}>
                <input
                  type="checkbox"
                  checked={selectedRxIds.length === rxHistory.length && rxHistory.length > 0}
                  onChange={selectAllRx}
                  style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#6366F1" }}
                />
                {selectedRxIds.length === rxHistory.length && rxHistory.length > 0 ? "Deselect All" : "Select All"}
              </label>
              {selectedRxIds.length > 0 && (
                <button
                  onClick={copySelectedToAdvice}
                  style={{
                    border: "none", cursor: "pointer", borderRadius: 8,
                    padding: "7px 14px", fontSize: 12, fontWeight: 700,
                    background: "#6366F1", color: "#fff",
                  }}>
                  📥 Copy {selectedRxIds.length} to Advice
                </button>
              )}
            </div>
          )}

          {/* List */}
          <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
            {rxHistory.length === 0 && (
              <div style={{ textAlign: "center", color: "#94A3B8", padding: "40px 0", fontSize: 14 }}>
                No past prescriptions found
              </div>
            )}
            {rxHistory.map((rx) => {
              const isSelected = selectedRxIds.includes(rx.prescriptionId);
              const isExpanded = expandedRxId === rx.prescriptionId;
              const date = new Date(rx.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
              const complaints = (rx.typed?.complaints || "").replace(/<[^>]+>/g, " ").trim();
              const diagnosis  = (rx.typed?.diagnosis  || "").replace(/<[^>]+>/g, " ").trim();
              const advice     = (rx.typed?.advice     || "").replace(/<[^>]+>/g, " ").trim();
              const notes      = (rx.typed?.notes      || "").replace(/<[^>]+>/g, " ").trim();

              return (
                <div key={rx.prescriptionId} style={{
                  border: isSelected ? "2px solid #6366F1" : "1px solid #E2E8F0",
                  borderRadius: 12, background: isSelected ? "#EEF2FF" : "#fff",
                  overflow: "hidden", transition: "all 0.15s",
                }}>
                  {/* Row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px" }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectRx(rx.prescriptionId)}
                      style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#6366F1", flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => setExpandedRxId(isExpanded ? null : rx.prescriptionId)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>{date}</span>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
                          background: rx.visitType === "followup" ? "#DBEAFE" : "#DCFCE7",
                          color: rx.visitType === "followup" ? "#1D4ED8" : "#166534",
                        }}>
                          {rx.visitType === "followup" ? "Follow-up" : "New Visit"}
                        </span>
                        {rx.visitNumber && <span style={{ fontSize: 11, color: "#94A3B8" }}>#{rx.visitNumber}</span>}
                      </div>
                      <p style={{ margin: "3px 0 0", fontSize: 12, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {complaints ? complaints.substring(0, 60) : "No complaints recorded"}
                      </p>
                    </div>
                    <button
                      onClick={() => setExpandedRxId(isExpanded ? null : rx.prescriptionId)}
                      style={{ border: "none", background: "none", cursor: "pointer", color: "#94A3B8", fontSize: 14, padding: "4px", flexShrink: 0 }}>
                      {isExpanded ? "▲" : "▼"}
                    </button>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div style={{ borderTop: "1px solid #E2E8F0", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8, background: isSelected ? "#F5F3FF" : "#FAFAFA" }}>
                      <p style={{ margin: 0, fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>Rx# {rx.prescriptionId}</p>
                      {complaints && (
                        <div>
                          <p style={{ margin: "0 0 2px", fontSize: 11, fontWeight: 700, color: "#6366F1" }}>COMPLAINTS</p>
                          <p style={{ margin: 0, fontSize: 12, color: "#374151", lineHeight: 1.5 }}>{complaints}</p>
                        </div>
                      )}
                      {diagnosis && (
                        <div>
                          <p style={{ margin: "0 0 2px", fontSize: 11, fontWeight: 700, color: "#059669" }}>DIAGNOSIS</p>
                          <p style={{ margin: 0, fontSize: 12, color: "#374151", lineHeight: 1.5 }}>{diagnosis}</p>
                        </div>
                      )}
                      {advice && (
                        <div>
                          <p style={{ margin: "0 0 2px", fontSize: 11, fontWeight: 700, color: "#D97706" }}>ADVICE / MEDICINES</p>
                          <p style={{ margin: 0, fontSize: 12, color: "#374151", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{advice}</p>
                        </div>
                      )}
                      {notes && (
                        <div>
                          <p style={{ margin: "0 0 2px", fontSize: 11, fontWeight: 700, color: "#64748B" }}>NOTES</p>
                          <p style={{ margin: 0, fontSize: 12, color: "#374151", lineHeight: 1.5 }}>{notes}</p>
                        </div>
                      )}
                      {!complaints && !diagnosis && !advice && !notes && (
                        <p style={{ margin: 0, fontSize: 12, color: "#94A3B8" }}>Handwritten prescription — no typed content</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ padding: "14px 20px", borderTop: "1px solid #F1F5F9", background: "#FAFAFA" }}>
            <p style={{ margin: 0, fontSize: 11, color: "#94A3B8", textAlign: "center" }}>
              ✅ Check prescriptions to copy their advice/medicine content into the current prescription
            </p>
          </div>
        </div>
      </div>
    )}

    <style>{`
      @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to   { transform: translateX(0); opacity: 1; }
      }
      /* Kill the yellow/tan browser-autofill tint on plain text fields
         (Allergies / Investigations / General Exam / Notes) — it was
         never an intentional design choice, just Chrome guessing these
         are fillable form fields. */
      input:-webkit-autofill, textarea:-webkit-autofill {
        -webkit-box-shadow: 0 0 0px 1000px #fff inset !important;
        box-shadow: 0 0 0px 1000px #fff inset !important;
        -webkit-text-fill-color: inherit !important;
      }

      /* Medicine row: 8-column dense grid on desktop, stacks to a
         readable single column on phones/small tablets so it doesn't
         render as an unusable squeeze. Uses plain CSS (not Tailwind
         arbitrary classes) so it doesn't depend on the project's
         Tailwind content-scanning config picking up a dynamic class. */
      .med-row-grid {
        display: grid;
        grid-template-columns: 1.5fr 0.8fr 1fr 0.7fr 1fr 1fr 0.9fr auto;
      }
      @media (max-width: 900px) {
        .med-row-grid { grid-template-columns: 1fr 1fr; }
      }
      @media (max-width: 480px) {
        .med-row-grid { grid-template-columns: 1fr; }
      }
    `}</style>
    <div className="max-w-[1600px] mx-auto p-4 space-y-4">

      {/* TOP BAR — 2-row compact layout */}
      <div style={{
        background: "#fff", borderRadius: 14,
        border: "1px solid #E5E7EB",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        padding: "10px 14px",
        display: "flex", flexDirection: "column", gap: 8,
      }}>

        {/* ROW 1 — Patient name + mode toggle + settings */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <h1 style={{ fontWeight: 700, fontSize: 15, color: "#1F2937", margin: 0, fontFamily: "'Inter',sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {patient.name} <span style={{ color: "#9CA3AF", fontWeight: 500 }}>• {patient.patientId}</span>
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <button className="btn" title="Prescription Settings"
              onClick={() => {
                const draft = { patientId, rxId, visitType, fromUrl: window.location.pathname + window.location.search, typed, pages: pagesRef.current, pageIndex, settingsSnapshot: settings };
                localStorage.setItem(draftKey, JSON.stringify(draft));
                navigate("/staff-portal/prescription-settings");
              }}
              style={{ padding: "6px 10px", fontSize: 15 }}>⚙️</button>
            <button
              onClick={loadHistory}
              disabled={historyLoading}
              title="View past prescriptions"
              style={{
                padding: "6px 12px", fontSize: 12, borderRadius: 8,
                border: "1px solid #D1FAE5", background: showHistory ? "#D1FAE5" : "#F0FDF4",
                color: "#065F46", fontWeight: 600, cursor: "pointer",
                fontFamily: "'Inter',sans-serif", whiteSpace: "nowrap",
              }}>
              {historyLoading ? "…" : "📋 History"}
            </button>
            <div style={{ display: "flex", background: "#F3F4F6", borderRadius: 8, padding: 3, gap: 2 }}>
              <button className={`btn ${mode === "typed" ? "btn-primary" : ""}`}
                onClick={() => setMode("typed")}
                style={{ padding: "5px 12px", fontSize: 12, borderRadius: 6 }}>⌨️ Typed</button>
              <button className={`btn ${mode === "handwritten" ? "btn-primary" : ""}`}
                onClick={() => setMode("handwritten")}
                style={{ padding: "5px 12px", fontSize: 12, borderRadius: 6 }}>✍️ Write</button>
            </div>
          </div>
        </div>

        {/* ROW 2 — Save + Complete + Print mode + Print + Pages */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "nowrap" }}>

          {/* Save + Complete */}
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button onClick={savePrescription} disabled={saving || completing}
              className="btn-primary"
              style={{ padding: "6px 14px", fontSize: 13, borderRadius: 8, whiteSpace: "nowrap" }}>
              {saving ? "Saving…" : "💾 Save"}
            </button>
            <button onClick={completeConsultation} disabled={saving || completing}
              style={{
                padding: "6px 14px", fontSize: 13, borderRadius: 8, border: "none",
                background: completing ? "#16a34a99" : "#16a34a",
                color: "#fff", fontWeight: 700,
                cursor: completing ? "not-allowed" : "pointer",
                fontFamily: "'Inter',sans-serif", whiteSpace: "nowrap",
              }}>
              {completing ? "⏳…" : "✅ Done"}
            </button>
          </div>

          {/* Print mode + Print button */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <div style={{ display: "flex", background: "#F3F4F6", borderRadius: 8, padding: 3, gap: 2 }}>
              <button className={`btn ${printMode==="typed"?"btn-primary":""}`}
                onClick={()=>setPrintMode("typed")}
                style={{ padding: "4px 10px", fontSize: 11, borderRadius: 6 }}>Typed</button>
              <button className={`btn ${printMode==="handwritten"?"btn-primary":""}`}
                onClick={()=>setPrintMode("handwritten")}
                style={{ padding: "4px 10px", fontSize: 11, borderRadius: 6 }}>Write</button>
              <button className={`btn ${printMode==="both"?"btn-primary":""}`}
                onClick={()=>setPrintMode("both")}
                style={{ padding: "4px 10px", fontSize: 11, borderRadius: 6 }}>Both</button>
            </div>
            <button className="btn"
              style={{ padding: "6px 12px", fontSize: 13, whiteSpace: "nowrap" }}
              onClick={() => {
                if (!patient || !settings) { alert("Please wait… loading patient/settings"); return; }
                const liveVectorPages = pagesRef.current.map(page => (page || []).filter(s => s?.points?.length));
                const safeSettings = {
                  ...settings,
                  headerBox: { ...settings.headerBox, content: sanitizePrintHTML(settings?.headerBox?.content || "") },
                  footerBox: { ...settings.footerBox, content: sanitizePrintHTML(settings?.footerBox?.content || "") },
                };
                const rxLive = {
                  typed, handwritten: { pages: liveVectorPages },
                  settings: safeSettings, patient, doctor: settings?.doctorDetails || {},
                  createdAt: new Date().toISOString(), prescriptionId: savedRxCode || "TEMP-RX",
                  modeUsed: printMode==="typed" ? ["typed"] : printMode==="handwritten" ? ["handwritten"] : ["typed","handwritten"],
                };
                const w = window.open(window.location.origin + `/staff-portal/prescription/print/live`, "_blank");
                const t = setInterval(() => { if (!w || w.closed) { clearInterval(t); return; } w.postMessage({ type:"RX_PRINT", payload:rxLive }, "*"); }, 300);
                setTimeout(() => clearInterval(t), 5000);
              }}>
              🖨 Print
            </button>
          </div>

          {/* Page controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
            <button className="btn" onClick={() => goToPage(pageIndex - 1)} style={{ padding: "4px 8px", fontSize: 13 }}>◀</button>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", whiteSpace: "nowrap", fontFamily: "'Inter',sans-serif" }}>
              {pageIndex + 1} / {pagesRef.current.length}
            </span>
            <button className="btn" onClick={() => goToPage(pageIndex + 1)} style={{ padding: "4px 8px", fontSize: 13 }}>▶</button>
            <button className="btn-primary" onClick={addPage} style={{ padding: "4px 10px", fontSize: 12, borderRadius: 8, whiteSpace: "nowrap" }}>➕ Page</button>
          </div>

        </div>
      </div>

    {/* TYPED MODE */}
{mode === "typed" && (
  <div
    ref={typedRef}
    className="bg-white rounded-xl border shadow-sm p-4 grid grid-cols-1 lg:grid-cols-[32%_68%] gap-4 lg:gap-5 items-start"
  >

    {/* ══════════════════════ LEFT COLUMN ══════════════════════ */}
    <div className="space-y-3">

      {/* ===== 1. CHIEF COMPLAINTS ===== */}
      <div className="bg-gray-50 border rounded-xl p-2">
        <div className="text-xs font-semibold text-gray-500 mb-1">1. CHIEF COMPLAINTS</div>
        <TypedEditor
          boxName="complaints"
          value={typed.complaints}
          onChange={(val)=> { setTyped({ ...typed, complaints: val }); setDraftTick(Date.now()); }}
          settings={getEditorStyle("complaints")}
        />
      </div>

      {/* ===== ALLERGIES (optional) ===== */}
      <div className="bg-gray-50 border rounded-xl p-2">
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-500" style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={showAllergies}
            onChange={(e) => setShowAllergies(e.target.checked)}
          />
          ALLERGIES (Optional)
        </label>
        {showAllergies && (
          <textarea
            className="input"
            autoComplete="off"
            style={{ width: "100%", fontSize: 13, marginTop: 6, minHeight: 50 }}
            placeholder="Enter allergies…"
            value={typed.allergies}
            onChange={(e) => { setTyped({ ...typed, allergies: e.target.value }); setDraftTick(Date.now()); }}
          />
        )}
      </div>

      {/* ===== INVESTIGATIONS (optional) ===== */}
      <div className="bg-gray-50 border rounded-xl p-2">
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-500" style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={showInvestigations}
            onChange={(e) => setShowInvestigations(e.target.checked)}
          />
          INVESTIGATIONS (Optional)
        </label>
        {showInvestigations && (
          <textarea
            className="input"
            autoComplete="off"
            style={{ width: "100%", fontSize: 13, marginTop: 6, minHeight: 50 }}
            placeholder="Enter investigations…"
            value={typed.investigations}
            onChange={(e) => { setTyped({ ...typed, investigations: e.target.value }); setDraftTick(Date.now()); }}
          />
        )}
      </div>

      {/* ===== GENERAL EXAMINATION (always visible, all optional, plain text — no dropdowns) ===== */}
      <div className="bg-gray-50 border rounded-xl p-2">
        <div className="text-xs font-semibold text-gray-500 mb-2">GENERAL EXAMINATION</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {["appetite", "bowel", "urination", "sleep", "sweating", "thirst"].map(field => (
            <div key={field}>
              <div style={{ fontSize: 11, color: "#6b7280", textTransform: "capitalize", marginBottom: 2 }}>
                {field === "thirst" ? "Thirst (Trishna)" : field}
              </div>
              <input
                className="input"
                autoComplete="off"
                style={{ fontSize: 12, width: "100%" }}
                placeholder="Type finding…"
                value={typed.generalExamination?.[field] || ""}
                onChange={(e) => updateGeneralExam(field, e.target.value)}
              />
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
          <div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>BP (mmHg)</div>
            <input
              className="input"
              autoComplete="off"
              style={{ fontSize: 12, width: "100%" }}
              placeholder="e.g. 120/80"
              value={typed.generalExamination?.bp || ""}
              onChange={(e) => updateGeneralExam("bp", e.target.value)}
            />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>Pulse (per min)</div>
            <input
              className="input"
              autoComplete="off"
              style={{ fontSize: 12, width: "100%" }}
              placeholder="e.g. 72"
              value={typed.generalExamination?.pulse || ""}
              onChange={(e) => updateGeneralExam("pulse", e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>

    {/* ══════════════════════ RIGHT COLUMN ══════════════════════ */}
    <div className="space-y-3">

      {/* ===== 2. DIAGNOSIS ===== */}
      <div className="bg-gray-50 border rounded-xl p-2">
        <div className="text-xs font-semibold text-gray-500 mb-1">2. DIAGNOSIS</div>
        <TypedEditor
          boxName="diagnosis"
          value={typed.diagnosis}
          onChange={(val)=> { setTyped({ ...typed, diagnosis: val }); setDraftTick(Date.now()); }}
          settings={getEditorStyle("diagnosis")}
        />
      </div>

      {/* ===== 3. TREATMENT ADVICE (MEDICINE) — structured rows ===== */}
      <div className="bg-gray-50 border rounded-xl p-2">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-gray-500">3. TREATMENT ADVICE (MEDICINE)</div>
          <button
            type="button"
            className="btn-primary"
            style={{ padding: "4px 10px", fontSize: 12, borderRadius: 8 }}
            onClick={addMedicineRow}
          >
            ➕ Add Medicine
          </button>
        </div>

        <div className="space-y-2">
          {medicines.map((med, idx) => (
            <div
              key={med.id}
              className="bg-white border rounded-lg p-2"
              style={{ position: "relative" }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", marginBottom: 4 }}>
                MEDICINE {idx + 1}
              </div>
              <div
                className="med-row-grid"
                style={{
                  gap: 6,
                  alignItems: "start",
                }}
              >
                <input
                  className="input"
                  placeholder="Medicine Name (type to search)"
                  value={med.name}
                  onFocus={() => setActiveMedicineRowId(med.id)}
                  onChange={(e) => updateMedicineField(med.id, "name", e.target.value)}
                  style={{ fontSize: 13 }}
                />
                <input
                  className="input"
                  placeholder="Dosage (e.g. 3 gm)"
                  value={med.dosage}
                  onChange={(e) => updateMedicineField(med.id, "dosage", e.target.value)}
                  style={{ fontSize: 13 }}
                />
                <BilingualSelect
                  placeholder="Frequency"
                  value={med.frequency}
                  options={FREQUENCY_OPTIONS}
                  onChange={(v) => updateMedicineField(med.id, "frequency", v)}
                />
                <input
                  className="input"
                  placeholder="Days"
                  value={med.days}
                  onChange={(e) => updateMedicineField(med.id, "days", e.target.value)}
                  style={{ fontSize: 13 }}
                />
                <BilingualSelect
                  placeholder="Anupana"
                  value={med.anupana}
                  options={ANUPANA_OPTIONS}
                  onChange={(v) => updateMedicineField(med.id, "anupana", v)}
                />
                <input
                  className="input"
                  placeholder="Notes (Optional)"
                  value={med.notes}
                  onChange={(e) => updateMedicineField(med.id, "notes", e.target.value)}
                  style={{ fontSize: 13 }}
                />
                <input
                  className="input"
                  placeholder="Remark (Optional)"
                  value={med.remark}
                  onChange={(e) => updateMedicineField(med.id, "remark", e.target.value)}
                  style={{ fontSize: 13 }}
                />
                <button
                  type="button"
                  className="btn"
                  title="Remove medicine"
                  onClick={() => removeMedicineRow(med.id)}
                  disabled={medicines.length <= 1}
                  style={{
                    padding: "6px 8px",
                    fontSize: 13,
                    color: "#b91c1c",
                    opacity: medicines.length <= 1 ? 0.4 : 1,
                    cursor: medicines.length <= 1 ? "not-allowed" : "pointer",
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Live preview of how this will print */}
        <div className="mt-2 p-2 border rounded-lg bg-white">
          <div className="text-[10px] font-semibold text-gray-400 mb-1">PREVIEW</div>
          <div
            style={{ fontSize: 13, fontFamily: "Times New Roman" }}
            dangerouslySetInnerHTML={{
              __html:
                buildAdviceHtml(medicines, legacyAdviceHtml) ||
                '<p style="color:#9ca3af;">No medicines added yet</p>',
            }}
          />
        </div>
      </div>

      {/* ===== 4. ADVICE TO PATIENT ===== */}
      <div className="bg-gray-50 border rounded-xl p-2">
        <div className="text-xs font-semibold text-gray-500 mb-1">4. ADVICE TO PATIENT</div>
        <TypedEditor
          boxName="patientAdvice"
          value={typed.patientAdvice}
          onChange={(val)=> { setTyped({ ...typed, patientAdvice: val }); setDraftTick(Date.now()); }}
          settings={getEditorStyle("patientAdvice")}
        />
      </div>

      {/* ===== 5. NOTES / REMARKS ===== */}
      <div className="bg-gray-50 border rounded-xl p-2">
        <div className="text-xs font-semibold text-gray-500 mb-1">5. NOTES / REMARKS (Optional)</div>
        <TypedEditor
          boxName="notes"
          value={typed.notes}
          onChange={(val)=> { setTyped({ ...typed, notes: val }); setDraftTick(Date.now()); }}
          settings={getEditorStyle("notes")}
        />
      </div>

      {/* ===== FUTURE PLACEHOLDER — PATHYA / APATHYA ===== */}
      <div className="border rounded-xl p-3" style={{ background: "#f9fafb", opacity: 0.7 }}>
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold text-gray-500">PATHYA – APATHYA</div>
          <span style={{ fontSize: 10, background: "#e5e7eb", color: "#6b7280", padding: "2px 8px", borderRadius: 999 }}>
            Coming Soon
          </span>
        </div>
        <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
          🔒 This section will connect to the Dravya database and be available soon.
        </div>
      </div>
    </div>
  </div>
)}

     {/* CANVAS */}
{mode === "handwritten" && (
  <div className="bg-white border rounded-xl shadow-sm p-3" style={{ overflow: "auto", maxHeight: "78vh" }}>
    {/* Eraser cursor indicator — position:fixed, deliberately OUTSIDE
        the zoom-transform wrapper below (a CSS transform on an
        ancestor would otherwise re-anchor position:fixed to that
        ancestor instead of the viewport). */}
    <div
      ref={eraserCursorRef}
      style={{
        position: "fixed",
        display: "none",
        borderRadius: "50%",
        border: "1.5px solid #ef4444",
        background: "rgba(239,68,68,0.12)",
        pointerEvents: "none",
        zIndex: 9998,
      }}
    />
    <div style={{ display: "inline-block", minWidth: "100%" }}>
      <div
        style={{
          position: "relative",
          width: "100%",
          display: "flex",
          justifyContent: "center"
        }}
      >
        {/* BASE CANVAS (saved strokes) */}
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            touchAction: "none",
            width: DRAW_WIDTH + "px",
            height: DRAW_HEIGHT + "px",
            pointerEvents: "none"
          }}
        />

        {/* LIVE CANVAS (zero-lag drawing layer) */}
        <canvas
          ref={liveCanvasRef}
          onPointerDown={tool === "eraser" && eraserMode === "stroke" ? strokeErasePointerDown : pointerDown}
          onPointerMove={tool === "eraser" && eraserMode === "stroke" ? strokeErasePointerMove : pointerMove}
          onPointerUp={tool === "eraser" && eraserMode === "stroke" ? strokeErasePointerUp : pointerUp}
          onPointerCancel={tool === "eraser" && eraserMode === "stroke" ? strokeErasePointerUp : pointerCancel}
          onPointerLeave={tool === "eraser" && eraserMode === "stroke" ? strokeErasePointerUp : pointerCancel}
          style={{
            width: DRAW_WIDTH + "px",
            height: DRAW_HEIGHT + "px",
            flexShrink: 0,
            touchAction: "none",
            willChange: "transform"
          }}
        />
      </div>
    </div>
  </div>
)}

      {/* TOOLBOX */}
      <div
        ref={toolboxRef}
       style={{
  position: "fixed",
  left: toolboxPos.x,
  top: toolboxPos.y,
  zIndex: 9999,
  touchAction: "none"
}}


        className="bg-white/90 backdrop-blur border rounded-2xl shadow-xl p-2 max-w-[90vw]"
      >
        {/* HEADER */}
       <div
  className="flex items-center justify-between gap-2 cursor-move select-none bg-gray-100 rounded-xl px-2 py-1"
  onPointerDown={startDrag}
>
  {/* DRAG HANDLE */}
  <div className="flex items-center gap-2">
    <span className="text-gray-500 text-sm">⋮⋮</span>
    <span className="text-xs font-semibold">✍️ Tools</span>
  </div>

  <button
    className="btn text-xs"
    onClick={(e) => {
      e.stopPropagation(); // prevents drag while clicking
      setToolbarOpen(!toolbarOpen);
    }}
  >
    {toolbarOpen ? "−" : "+"}
  </button>
</div>

       {toolbarOpen && (
  <div
    className={`mt-2 flex gap-2 ${
      isVertical
        ? "flex-col items-start"
        : "items-center flex-wrap"
    }`}
  >


            <button className="btn" onClick={() => setTool("pen")}>
              🖊
            </button>
            <button className="btn" onClick={() => setTool("pencil")}>
              ✏️
            </button>
            <button className="btn" onClick={() => setTool("eraser")}>
              🧽
            </button>

            <button className="btn" onClick={undo}>↶</button>
            <button className="btn" onClick={redo}>↷</button>

            {/* Zoom controls — canvas mode only */}
            {mode === "handwritten" && (
              <div className={`flex gap-1 items-center ${isVertical ? "" : "ml-1"}`}>
                <button
                  type="button"
                  className="btn"
                  title="Zoom out"
                  onClick={() => setCanvasZoom(z => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))}
                  style={{ padding: "4px 8px", fontSize: 13 }}
                >
                  −
                </button>
                <span style={{ fontSize: 11, color: "#6b7280", minWidth: 34, textAlign: "center" }}>
                  {Math.round(canvasZoom * 100)}%
                </span>
                <button
                  type="button"
                  className="btn"
                  title="Zoom in"
                  onClick={() => setCanvasZoom(z => Math.min(2.5, Math.round((z + 0.25) * 100) / 100))}
                  style={{ padding: "4px 8px", fontSize: 13 }}
                >
                  +
                </button>
                {canvasZoom !== 1 && (
                  <button
                    type="button"
                    className="btn"
                    title="Reset zoom"
                    onClick={() => setCanvasZoom(1)}
                    style={{ padding: "4px 8px", fontSize: 11 }}
                  >
                    Reset
                  </button>
                )}
              </div>
            )}

          <div
  className={`flex gap-2 ${
    isVertical
      ? "flex-col items-start ml-0"
      : "items-center ml-2 flex-wrap"
  }`}
>

              <label className="text-xs">Pen</label>
              <input
                type="color"
                value={penColor}
                onChange={e => setPenColor(e.target.value)}
              />

              <label className="text-xs">Pencil</label>
              <input
                type="color"
                value={pencilColor}
                onChange={e => setPencilColor(e.target.value)}
              />

              <label className="text-xs">Pen Size</label>
              <input
                type="range"
                min="1"
                max="10"
                value={penStroke}
                onChange={e =>
                  setPenStroke(Number(e.target.value))
                }
              />

              <label className="text-xs">Pencil Size</label>
              <input
                type="range"
                min="1"
                max="10"
                value={pencilStroke}
                onChange={e =>
                  setPencilStroke(Number(e.target.value))
                }
              />

              <label className="text-xs">Eraser</label>
              <input
                type="range"
                min="5"
                max="60"
                value={eraserWidth}
                onChange={e =>
                  setEraserWidth(Number(e.target.value))
                }
              />

              {/* Eraser mode toggle — only meaningful while eraser is selected */}
              {tool === "eraser" && (
                <div className="flex gap-1 items-center" style={{ marginLeft: isVertical ? 0 : 4 }}>
                  <button
                    type="button"
                    className="btn"
                    title="Erase pixels under the eraser (existing behaviour)"
                    onClick={() => setEraserMode("pixel")}
                    style={{
                      padding: "3px 8px", fontSize: 11,
                      background: eraserMode === "pixel" ? "#111827" : "#fff",
                      color: eraserMode === "pixel" ? "#fff" : "#111827",
                    }}
                  >
                    Pixel
                  </button>
                  <button
                    type="button"
                    className="btn"
                    title="Tap a stroke to delete it whole — no ragged fragments"
                    onClick={() => setEraserMode("stroke")}
                    style={{
                      padding: "3px 8px", fontSize: 11,
                      background: eraserMode === "stroke" ? "#111827" : "#fff",
                      color: eraserMode === "stroke" ? "#fff" : "#111827",
                    }}
                  >
                    Whole Stroke
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Medicine Suggestion Panel (only shown if doctor has hasMedicineSuggestions) ──
          Scoped per medicine row: whichever row's Medicine Name field is
          currently focused drives the "Dravyas" search, so switching rows
          always starts that row's ingredient search from zero. ── */}
      <MedicineSuggestionPanel
        complaintsHtml={typed.complaints}
        adviceHtml={typed.advice}
        dravyaQueryText={
          medicines.find(m => m.id === activeMedicineRowId)?.name || ""
        }
        doctorUser={doctorUser}
      />

      {/* ── Disease Suggestion Panel (only shown if doctor has hasDiseaseSuggestions) ── */}
      <DiseaseSuggestionPanel
        complaintsHtml={typed.complaints}
        doctorUser={doctorUser}
        onInsertDiagnosis={(name) => {
          setTyped(prev => ({ ...prev, diagnosis: (prev.diagnosis || "") + "<p>" + name + "</p>" }));
          setDraftTick(Date.now());
        }}
      />
    </div>
    </>
  );
}
